import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HeroMark } from "../site/HeroMark";
import { RevealLines, EASE } from "../site/motion";
import { MagneticButton } from "../site/Buttons";
import { PILLARS } from "@/lib/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const markY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const spot = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={spot} data-testid="hero-section" className="hero-bg relative min-h-[100svh] overflow-hidden pt-28 md:pt-32">
      <div className="hex-pattern pointer-events-none absolute inset-0" />
      <div className="hero-spot pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1440px] gap-6 px-6 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: markY }} className="order-1 mx-auto w-[62vw] max-w-[260px] lg:order-2 lg:col-span-5 lg:mt-6 lg:w-full lg:max-w-none">
          <HeroMark />
        </motion.div>
        <motion.div style={{ y: textY, opacity: fade }} className="order-2 lg:order-1 lg:col-span-7 lg:pt-16">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-skaris-sky">
            [ Skaris Technology LLP — IT · Digital · AI ]
          </motion.p>
          <RevealLines
            lines={["Built for", "your business.", "Engineered", "to perform."]}
            delay={0.75}
            className="mt-6 font-display text-[3.2rem] font-semibold uppercase leading-[0.9] tracking-[-0.035em] text-cream sm:text-7xl lg:text-[7.4rem]"
            lineClass={(i) => (i === 2 ? "text-gradient" : i === 3 ? "text-cream/90" : "")}
          />
        </motion.div>
      </div>

      <div className="relative mx-auto mt-12 max-w-[1440px] px-6 pb-10 md:px-10 lg:mt-4">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.45, duration: 1, ease: EASE }}
          className="grid gap-10 border-t border-cream/15 pt-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="max-w-md font-body text-base leading-relaxed text-cream/80">
              A lean, delivery-focused team building custom software, digital platforms and AI-enabled systems — from requirements to deployment and beyond.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <MagneticButton to="/contact" testid="hero-cta-primary">Start a project</MagneticButton>
              <MagneticButton to="/services" variant="ghost" testid="hero-cta-secondary">Our services</MagneticButton>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-cream/10 bg-cream/10 sm:grid-cols-3 lg:col-span-7">
            {PILLARS.map((p) => (
              <a key={p.id} href="#pillars" data-testid={`hero-pillar-${p.id}`} className="group bg-skaris-ink/70 p-5 backdrop-blur-md transition-colors duration-500 hover:bg-skaris-navy/80">
                <span className="font-mono text-[11px] text-skaris-sky">{p.n}</span>
                <p className="mt-6 font-display text-lg font-medium text-cream">{p.title}</p>
                <span className="mt-2 block h-px w-6 bg-cream/40 transition-[width] duration-500 group-hover:w-full" />
              </a>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="mt-8 hidden items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/60 md:flex">
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll to explore
        </motion.div>
      </div>
    </section>
  );
};
